import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEyeSlash, faExpandArrowsAlt } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import Question from '../../components/Question';

// Import rune images
import fehuImage from '../../../img/runes/image-000.webp';
import uruzImage from '../../../img/runes/image-001.webp';
import thurisazImage from '../../../img/runes/image-002.webp';
import ansuzImage from '../../../img/runes/image-003.webp';
import raidhoImage from '../../../img/runes/image-004.webp';
import kaunanImage from '../../../img/runes/image-005.webp';
import geboImage from '../../../img/runes/image-006.webp';
import wunjoImage from '../../../img/runes/image-007.webp';
import hagalazImage from '../../../img/runes/image-008.webp';
import nauthizImage from '../../../img/runes/image-009.webp';
import isaImage from '../../../img/runes/image-010.webp';
import jeraImage from '../../../img/runes/image-011.webp';
import eiwazImage from '../../../img/runes/image-012.webp';
import perthroImage from '../../../img/runes/image-013.webp';
import algizImage from '../../../img/runes/image-014.webp';
import sowiloImage from '../../../img/runes/image-015.webp';
import tiwazImage from '../../../img/runes/image-016.webp';
import berkanoImage from '../../../img/runes/image-017.webp';
import ehwazImage from '../../../img/runes/image-018.webp';
import mannazImage from '../../../img/runes/image-019.webp';
import laguzImage from '../../../img/runes/image-020.webp';
import ingwazImage from '../../../img/runes/image-021.webp';
import dagazImage from '../../../img/runes/image-022.webp';
import othalaImage from '../../../img/runes/image-023.webp';

const RunesStructureLesson = ({ 
  level = 'beginner', 
  onNextLesson, 
  markLessonComplete,
  currentQuestion = 0,
  onQuestionChange,
  currentLesson,
  completedQuestions = [],
  setCompletedQuestions
}) => {
  const sections = [
    {
      id: 'introduction',
      title: 'I. Introduction to the Elder Futhark',
      content: `
        The Elder Futhark is the oldest form of the runic alphabets, used by Germanic tribes from the 2nd to the 8th century. 
        Consisting of 24 runes, this ancient system served not only as a method of writing but also as a profound tool for divination, magic, and spiritual guidance. 
        The term "Futhark" itself is derived from the first six letters of this rune set: Fehu, Uruz, Thurisaz, Ansuz, Raidho, and Kaunan.
        Each rune embodies deep symbolic meaning, connecting the practitioner to the forces of nature, the cosmos, and the inner self.
      `,
      imageUrl: fehuImage,
      questions: {
        beginner: {
          question: "How many runes are in the Elder Futhark?",
          options: ["16", "24", "28", "32"],
          correctAnswer: "24",
        },
        intermediate: {
          question: "What is the origin of the term 'Futhark'?",
          options: [
            "A combination of the first six runes",
            "A Nordic god",
            "A Germanic tribe",
            "An ancient city",
          ],
          correctAnswer: "A combination of the first six runes",
        },
      },
    },
    {
      id: 'division',
      title: 'II. Division into Aetts',
      content: `
        The 24 runes of the Elder Futhark are divided into three distinct groups known as Aetts, each containing eight runes. 
        These groups are referred to as Freyr's Aett, Hagal's Aett, and Tyr's Aett, named after prominent deities in the Norse pantheon. 
        Each Aett represents a different aspect of the human experience and spiritual journey:
        - Freyr's Aett: The first eight runes, connected with wealth, creation, and new beginnings.
        - Hagal's Aett: The second set of eight runes, associated with challenges, transformation, and natural forces.
        - Tyr's Aett: The final eight runes, symbolizing justice, sacrifice, and spiritual victory.
      `,
      imageUrl: uruzImage,
      questions: {
        beginner: {
          question: "How many Aetts are in the Elder Futhark?",
          options: ["1", "2", "3", "4"],
          correctAnswer: "3",
        },
        intermediate: {
          question: "What does Freyr's Aett primarily represent?",
          options: [
            "Wealth and creation",
            "Challenges and transformation",
            "Justice and sacrifice",
            "War and conflict",
          ],
          correctAnswer: "Wealth and creation",
        },
      },
    },
    {
      id: 'symbolism',
      title: 'III. Symbolism and Meanings',
      content: `
        Each rune in the Elder Futhark carries a unique symbolism that reflects its role in ancient Norse society. 
        These symbols often represent elements of nature, animals, gods, and spiritual concepts, making the runes deeply embedded in the worldview of the time.
        - Fehu: Symbolizes wealth and prosperity, often associated with cattle, which were a measure of wealth in ancient times.
        - Uruz: Represents primal strength, physical health, and the untamed power of the wild aurochs.
        - Thurisaz: Associated with the giants (Thurses), this rune symbolizes both destructive and protective forces.
        The meanings of the runes can be layered and complex, often providing insight into situations both mundane and spiritual.
      `,
      imageUrl: thurisazImage,
      questions: {
        beginner: {
          question: "What does the rune Fehu symbolize?",
          options: ["Wealth and prosperity", "War and conflict", "Love and relationships", "Knowledge and wisdom"],
          correctAnswer: "Wealth and prosperity",
        },
        intermediate: {
          question: "Which rune is associated with primal strength and health?",
          options: ["Ansuz", "Uruz", "Gebo", "Eiwaz"],
          correctAnswer: "Uruz",
        },
      },
    },
    {
      id: 'use',
      title: 'IV. Use in Divination and Magic',
      content: `
        The Elder Futhark was not just a writing system; it was also a powerful tool in divination and magical practices. 
        Runes were often inscribed on talismans, weapons, and tools to imbue them with specific energies. 
        In divination, practitioners would cast the runes and interpret their positions to gain insight into the past, present, or future. 
        Each rune could influence the reading based on its symbolic meaning, orientation (upright or reversed), and relationship with other runes in the casting.
        - Fehu could indicate financial gain or loss, depending on its position.
        - Thurisaz might warn of an impending challenge or suggest the need for defense.
        The practice of runecasting continues to be a respected spiritual tool in modern times, offering guidance and clarity to those who seek it.
      `,
      imageUrl: ansuzImage,
      questions: {
        beginner: {
          question: "In addition to writing, what were the Elder Futhark runes commonly used for?",
          options: ["Cooking", "Combat training", "Divination and magic", "Building construction"],
          correctAnswer: "Divination and magic",
        },
        intermediate: {
          question: "How might the rune Thurisaz be interpreted in a divination?",
          options: [
            "Financial gain",
            "A coming challenge",
            "A journey",
            "A new relationship",
          ],
          correctAnswer: "A coming challenge",
        },
      },
    },
    {
      id: 'legacy',
      title: 'V. The Legacy of the Elder Futhark',
      content: `
        The influence of the Elder Futhark extends far beyond its original time and place. 
        As the precursor to later runic alphabets, such as the Younger Futhark and Anglo-Saxon Futhorc, its legacy can be seen in various aspects of modern culture, including literature, art, and spiritual practices.
        The revival of interest in runes during the 20th century, particularly within the neopagan and esoteric communities, has brought the Elder Futhark back into the spotlight.
        Today, the runes are studied not only for their historical significance but also for their spiritual insights and practical applications in areas such as meditation, self-reflection, and personal growth.
      `,
      imageUrl: raidhoImage,
      questions: {
        beginner: {
          question: "What is the primary legacy of the Elder Futhark?",
          options: [
            "Its influence on modern cooking techniques",
            "Its role in the development of later runic alphabets",
            "Its use in 20th-century warfare",
            "Its contribution to modern technology",
          ],
          correctAnswer: "Its role in the development of later runic alphabets",
        },
        intermediate: {
          question: "Which modern communities have revived interest in the Elder Futhark?",
          options: [
            "Neopagan and esoteric communities",
            "Medical and scientific communities",
            "Military and defense communities",
            "Agricultural and environmental communities",
          ],
          correctAnswer: "Neopagan and esoteric communities",
        },
      },
    },
  ];

  const [selectedOption, setSelectedOption] = useState('');
  const [imageVisible, setImageVisible] = useState(true);
  const [imageSize, setImageSize] = useState('smallimage');
  const [allQuestionsAnsweredCorrectly, setAllQuestionsAnsweredCorrectly] = useState(false);

  useEffect(() => {
    if (allQuestionsAnsweredCorrectly) {
      markLessonComplete();
      onNextLesson();
    }
  }, [allQuestionsAnsweredCorrectly, markLessonComplete, onNextLesson]);

  const handleAnswer = (isCorrect) => {
    if (isCorrect) {
      const newCompletedQuestions = [...completedQuestions];
      newCompletedQuestions[currentQuestion] = true;
      setCompletedQuestions(newCompletedQuestions);
      
      if (currentQuestion + 1 < sections.length) {
        onQuestionChange(currentQuestion + 1);
      } else {
        setAllQuestionsAnsweredCorrectly(true);
      }
    }
  };

  const toggleImageVisibility = () => {
    setImageVisible(!imageVisible);
  };

  const toggleImageSize = () => {
    setImageSize(imageSize === 'smallimage' ? 'largeimage' : 'smallimage');
  };

  const getImageStyle = () => {
    const baseStyle = {
      height: 'auto',
      borderRadius: '8px',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
      transition: 'all 0.3s ease-in-out'
    };

    if (imageSize === 'smallimage') {
      return {
        ...baseStyle,
        maxWidth: '150px',
        width: '150px'
      };
    } else {
      return {
        ...baseStyle,
        maxWidth: '300px',
        width: '300px'
      };
    }
  };

  const transition = { duration: 0.15, ease: "easeInOut" };

  return (
    <div>
      <Helmet>
        <title>Elder Futhark Structure - {level.charAt(0).toUpperCase() + level.slice(1)} Lesson</title>
      </Helmet>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestion}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={transition}
        >
          {imageVisible && sections[currentQuestion]?.imageUrl && (
            <div className="has-text-centered mb-4">
              <img
                src={sections[currentQuestion].imageUrl}
                alt={`Elder Futhark Rune - ${sections[currentQuestion].title}`}
                style={getImageStyle()}
              />
              <div className="icon-buttons mt-2">
                <FontAwesomeIcon
                  icon={faEyeSlash}
                  className="icon-button has-text-warning mr-3"
                  onClick={toggleImageVisibility}
                  style={{ cursor: 'pointer' }}
                />
                <FontAwesomeIcon
                  icon={faExpandArrowsAlt}
                  className="icon-button has-text-warning"
                  onClick={toggleImageSize}
                  style={{ cursor: 'pointer' }}
                />
              </div>
            </div>
          )}

          <section className="section has-text-centered" id={sections[currentQuestion]?.id}>
            <div className="container content">
              <div className="columns">
                <div className="column">
                  <h2 className="has-text-primary">{sections[currentQuestion]?.title}</h2>
                  <p className="has-text-white">{sections[currentQuestion]?.content}</p>
                </div>
              </div>
            </div>
          </section>

          {sections[currentQuestion]?.questions && (
            <section className="section has-text-centered">
              <div className="container content">
                <div className="columns">
                  <div className="column">
                    <Question
                      question={sections[currentQuestion].questions[level].question}
                      options={sections[currentQuestion].questions[level].options}
                      correctAnswer={sections[currentQuestion].questions[level].correctAnswer}
                      onAnswered={handleAnswer}
                    />
                  </div>
                </div>
              </div>
            </section>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default RunesStructureLesson;
